import Sentry from "integrations/sentry"
import LogRocket from "integrations/logrocket"
import { clientEnv } from "config"
import type { PurchasePaymentMethod } from "app/bookings/booking-utils"

LogRocket.getSessionURL((sessionURL) => {
  Sentry.configureScope((scope) => {
    scope.setExtra("sessionURL", sessionURL)
  })
})

class Tracker {
  userId?: number
  orgId?: number
  email?: string

  setUserId(id: number) {
    try {
      if (!clientEnv.isProduction || !id) return
      if (id === this.userId) return
      this.userId = id
      Sentry.setUser({ id: id.toString() })
      LogRocket.identify(id.toString())
    } catch (error) {
      console.log("Tracking.setUserId failed", error)
    }
  }

  setOrgId(orgId: number) {
    try {
      if (!clientEnv.isProduction || !orgId) return
      if (orgId === this.orgId) return
      this.orgId = orgId
      Sentry.setUser({ orgId: orgId.toString() })
      LogRocket.identify(this.userId?.toString()! || this.email!, {
        orgId,
        email: this.email!,
      })
    } catch (error) {
      console.log("Tracking.setOrgId failed", error)
    }
  }

  setEmail(email: string) {
    try {
      if (!clientEnv.isProduction) return
      if (email === this.email) return
      this.email = email
      Sentry.setUser({ email })
      LogRocket.identify(this.userId?.toString()! || this.email!, {
        email,
        orgId: this.orgId!,
      })
    } catch (error) {
      console.log("Tracking.setEmail failed", error)
    }
  }

  /**
   * Report a completed checkout to ad/affiliate platforms (Google Analytics, Meta Pixel, Refersion).
   * `value` is the order grand total; `amount` is what was actually charged (used by Refersion).
   * `paymentMethod` is decided server-side by the checkout mutation. ClassWallet purchases are
   * reported before ClassWallet approves payment, so filter on `payment_type` for paid revenue.
   * `usedDiscount` (coupon code applied) and `usedVouchers` (voucher code applied) are reported
   * separately so eg. a card payment with a partial voucher stays "card".
   */
  trackPurchase({
    orderId,
    value,
    amount,
    itemName,
    paymentMethod,
    usedDiscount,
    usedVouchers,
  }: {
    orderId: number
    value: number
    amount: number
    itemName: string
    paymentMethod: PurchasePaymentMethod
    usedDiscount: boolean
    usedVouchers: boolean
  }) {
    try {
      if (window.location.hostname === "localhost") return
      logGAPurchase({ orderId, value, itemName, paymentMethod })
      if (window["gtag"]) {
        window["gtag"]("event", "purchase", {
          transaction_id: orderId.toString(),
          currency: "USD",
          value,
          payment_type: paymentMethod,
          used_discount: usedDiscount,
          used_vouchers: usedVouchers,
          items: [{ item_id: itemName, item_name: itemName, price: value, quantity: 1 }],
        })
      }
      if (window["fbq"]) {
        window["fbq"](
          "track",
          "Purchase",
          {
            currency: "USD",
            value,
            payment_type: paymentMethod,
            used_discount: usedDiscount,
            used_vouchers: usedVouchers,
          },
          // eventID lets Meta dedupe repeat reports of the same order
          { eventID: orderId.toString() }
        )
      }
      if (window["_rfsn"]) {
        window["_rfsn"]._addTrans({
          orderId,
          currency_code: "USD",
        })
        window["_rfsn"]._addItem({
          sku: "booking",
          quantity: "1",
          price: amount,
        })
        window["_rfsn"]._sendConversion()
        console.log("refersion success")
      }
    } catch (error) {
      console.log("Tracking.trackPurchase failed", error)
    }
  }
}

/**
 * Server-log each purchase sent to GA so DB orders can be reconciled with GA reports.
 * `gaLoaded` is false when gtag.js was blocked (ad blockers, tracking prevention), in which
 * case GA never receives the event. `gaClientId` comes from the _ga cookie and matches
 * GA's client ID for finding the visitor in GA.
 */
function logGAPurchase({
  orderId,
  value,
  itemName,
  paymentMethod,
}: {
  orderId: number
  value: number
  itemName: string
  paymentMethod: PurchasePaymentMethod
}) {
  try {
    const gaCookie = document.cookie.match(/(?:^|; )_ga=GA\d\.\d\.([^;]+)/)
    void fetch("/api/analytics/purchase", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // keepalive lets the request finish even if the page navigates away (eg. ClassWallet redirect)
      keepalive: true,
      body: JSON.stringify({
        orderId,
        value,
        itemName,
        paymentMethod,
        gaLoaded: !!window["google_tag_manager"],
        gaClientId: gaCookie ? gaCookie[1] : null,
      }),
    }).catch((error) => console.log("logGAPurchase failed", error))
  } catch (error) {
    console.log("logGAPurchase failed", error)
  }
}

export const Tracking = new Tracker()
